import type { LatLngBoundsExpression } from 'leaflet';
import type { GeoPoint, TrackingSegment } from '../types/tracking';
import { ROUTE_COLORS } from './constants';
import { mapAppleIdentifier } from './appleDevices';

/**
 * Return the `GeoPoint[]` to use for animation / distance calculations on
 * this segment. When the OSRM feature is on AND a correction has been
 * applied AND the corrected coordinates are present, we substitute the
 * OSRM coordinates for the raw ones — so the playback marker follows
 * the corrected path and the running distance readout reflects the
 * corrected distance. Otherwise the raw `segment.points` pass through
 * unchanged.
 *
 * Timestamps are interpolated evenly across the OSRM path between
 * `segment.start_time` and `segment.end_time` (same convention the
 * server mapper uses for raw coords in `interpolateTimestamps`). One
 * point returns just `start_time` to avoid a divide-by-zero.
 */
export function buildEffectiveSegmentPoints(
  segment: TrackingSegment,
  osrmEnabled: boolean,
): GeoPoint[] {
  const useOsrm =
    osrmEnabled &&
    segment.is_osrm_applied === true &&
    Array.isArray(segment.osrm_path) &&
    segment.osrm_path.length > 0;
  if (!useOsrm) return segment.points;

  const path = segment.osrm_path as [number, number][];
  const startMs = new Date(segment.start_time).getTime();
  const endMs = new Date(segment.end_time).getTime();
  const validRange = Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs;
  return path.map(([lng, lat], i) => ({
    longitude: lng,
    latitude: lat,
    recorded_at:
      path.length === 1 || !validRange
        ? segment.start_time
        : new Date(startMs + ((endMs - startMs) * i) / (path.length - 1)).toISOString(),
    segment_index: segment.index,
  }));
}

/**
 * Return the distance (meters) that counts toward the user's day total
 * for this segment. Same rule as `buildEffectiveSegmentPoints` —
 * applied corrections replace the raw haversine sum with the OSRM
 * total; everything else uses raw. Symmetrical with what the backend's
 * `get_report()` computes for `total_modified_distance_meters` so the
 * live-tracking totals agree with the Reports view.
 */
export function effectiveSegmentDistanceMeters(
  segment: TrackingSegment,
  osrmEnabled: boolean,
): number {
  const useOsrm =
    osrmEnabled &&
    segment.is_osrm_applied === true &&
    typeof segment.osrm_total_distance === 'number';
  return useOsrm
    ? (segment.osrm_total_distance as number)
    : (segment.total_distance_meters ?? 0);
}

/** Haversine distance between two lat/lng points in km */
export function haversineDistance(
  lat1: number, lon1: number,
  lat2: number, lon2: number
): number {
  const R = 6371; // Earth radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Build Leaflet bounds from an array of GeoPoints */
export function getBoundsFromPoints(points: GeoPoint[]): LatLngBoundsExpression | null {
  if (points.length === 0) return null;
  const lats = points.map(p => p.latitude);
  const lngs = points.map(p => p.longitude);
  return [
    [Math.min(...lats), Math.min(...lngs)],
    [Math.max(...lats), Math.max(...lngs)],
  ];
}

/** Get a color from the palette by index */
export function getRouteColor(index: number): string {
  return ROUTE_COLORS[index % ROUTE_COLORS.length];
}

/** Convert GeoPoints to Leaflet LatLng tuples */
export function geoPointsToLatLngs(points: GeoPoint[]): [number, number][] {
  return points.map(p => [p.latitude, p.longitude]);
}

/** Calculate bearing between two points (for arrow direction) */
export function calculateBearing(
  lat1: number, lon1: number,
  lat2: number, lon2: number
): number {
  const dLon = toRad(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Get initials from a full name string (e.g. "John Doe" -> "JD") */
export function getInitials(name?: string): string {
  const parts = (name || '').trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return `${first}${last}`.toUpperCase() || '?';
}

/**
 * Format a user's display label as `Name (RGF_ID)`. Two changemakers can
 * share the same name, so the RGF id disambiguates them. When the id is
 * missing or empty, returns the bare name (no empty parens). The name
 * itself falls back to "Unknown" if blank so the UI never renders an
 * empty label.
 */
export function formatUserLabel(
  name: string | null | undefined,
  employeeId: string | null | undefined,
): string {
  const safeName = (name ?? '').trim() || 'Unknown';
  const id = (employeeId ?? '').trim();
  return id ? `${safeName} (${id})` : safeName;
}

/**
 * Format a phone as `Manufacturer Model` (e.g. "Samsung Galaxy A52"),
 * dropping either part if missing and avoiding the duplicate when a
 * manufacturer string is already a prefix of the model. Returns `null`
 * when neither is available so callers can omit the line entirely.
 *
 * iOS reports `UIDevice.model` as an internal identifier like
 * `iPhone13,3` rather than a product name. When the model matches that
 * pattern we translate to the friendly name (`iPhone 12 Pro`) and drop
 * the manufacturer entirely — saying "iPhone 12 Pro" already implies
 * Apple. Unknown identifiers fall through to the raw string so the
 * reviewer at least sees what the device reported.
 */
export function formatDeviceLabel(
  manufacturer: string | null | undefined,
  model: string | null | undefined,
): string | null {
  const mfr = (manufacturer ?? '').trim();
  const mdl = (model ?? '').trim();
  if (!mfr && !mdl) return null;
  const friendly = mapAppleIdentifier(mdl);
  if (friendly) return friendly;
  if (!mfr) return mdl;
  if (!mdl) return mfr;
  // "Samsung" + "Samsung Galaxy A52" → "Samsung Galaxy A52" (no double-up).
  if (mdl.toLowerCase().startsWith(mfr.toLowerCase())) return mdl;
  return `${mfr} ${mdl}`;
}

/** Interpolate position between two points */
export function interpolatePosition(
  p1: [number, number],
  p2: [number, number],
  t: number // 0-1
): [number, number] {
  return [
    p1[0] + (p2[0] - p1[0]) * t,
    p1[1] + (p2[1] - p1[1]) * t,
  ];
}
